import { useEffect, useState } from 'react';
import { api, API, getTokens } from '../../lib/api';

/*
 * Quién tiene abierto un documento (como los avatares de Google Docs). Cada pestaña late cada 25 s a
 * `POST /office/presencia/{id}` y recibe quién más está; al cerrarse avisa con `…/salir` (si no, el
 * servidor la olvida sola a los 90 s). Backend: presencia_docs.py.
 *
 * Quien entra por el enlace sin cuenta tiene un animal mitológico ("Grifo anónimo"). Su identidad la
 * da el backend en la config del editor y se guarda en sessionStorage por archivo: al recargar sigue
 * siendo el mismo animal; en otra pestaña o al volver otro día, uno nuevo (como en Google).
 */

const LATIDO_MS = 25000;
const clave = (fileId) => `orvian_invitado:${fileId}`;

export function invitadoGuardado(fileId) {
  try {
    const guardado = JSON.parse(sessionStorage.getItem(clave(fileId)) || 'null');
    return guardado && /^invitado-\d{2}-[a-f0-9]{12}$/.test(guardado.id || '') ? guardado : null;
  } catch {
    return null;
  }
}

export function guardarInvitado(fileId, identidad) {
  try {
    if (identidad?.id) sessionStorage.setItem(clave(fileId), JSON.stringify(identidad));
  } catch {
    /* sin sessionStorage: se le asignará otro animal al recargar */
  }
}

export const ROL_PRESENCIA = {
  owner: 'Propietario',
  editor: 'Puede editar',
  commenter: 'Puede comentar',
  viewer: 'Solo puede ver',
};

/**
 * `{yo, presentes}` del documento. `activo` en false no late (p. ej. un invitado que aún no tiene animal:
 * lo espera de la config, para que el editor y los avatares digan lo mismo).
 */
export function usePresencia({ fileId, linkToken, invitado, activo = true }) {
  const [estado, setEstado] = useState({ yo: null, presentes: [] });
  const invitadoId = invitado?.id || null;

  useEffect(() => {
    if (!activo || !fileId) return undefined;
    let vivo = true;
    const cuerpo = { link: linkToken || undefined, invitado: invitadoId || undefined };

    const latir = async () => {
      try {
        const { data } = await api.post(`/office/presencia/${fileId}`, cuerpo, { skipAuthRedirect: true });
        if (vivo) setEstado({ yo: data.yo || null, presentes: data.presentes || [] });
      } catch {
        /* sin red o enlace cerrado: los avatares se quedan como estaban; el editor ya avisa de lo grave */
      }
    };

    // Al cerrar o salir del documento: fetch con keepalive (sendBeacon no manda la cabecera de sesión)
    let salio = false;
    const salir = () => {
      if (salio) return;
      salio = true;
      const { access } = getTokens();
      try {
        fetch(`${API}/office/presencia/${fileId}/salir`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', ...(access ? { Authorization: `Bearer ${access}` } : {}) },
          body: JSON.stringify(cuerpo),
        }).catch(() => {});
      } catch {
        /* el navegador ya no deja mandar nada: el servidor lo olvida solo */
      }
    };

    const alVolver = () => {
      if (!document.hidden) latir();
    };

    latir();
    const reloj = setInterval(latir, LATIDO_MS);
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('pagehide', salir);
    return () => {
      vivo = false;
      clearInterval(reloj);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('pagehide', salir);
      salir();
    };
  }, [fileId, linkToken, invitadoId, activo]);

  return estado;
}
